/**
 * HomePage (Главная, Frame 1) — branded header, referral strip, profile card,
 * 3-currency balance, the gold Shop tile and a list of sections.
 *
 * The Telegram BackButton is hidden at the root. Data (profile + balance) comes from
 * ``GET /api/me/`` — server-authoritative.
 */
import type { ReactNode } from "react";

import { openTelegramLink, useBackButton } from "@melusine/shared-telegram";
import { useMe } from "@entities/user";
import { BotActivationBanner } from "@widgets/bot-activation";
import { BalanceCard } from "@widgets/balance-card";
import { ProfileCard } from "@widgets/profile-card";
import { ShopClosedBanner } from "@widgets/shop-status";
import { StatePanel } from "@widgets/catalog-browser";
import { routes, useNavigate } from "@shared/router";

import styles from "./HomePage.module.css";

const Chevron = (): JSX.Element => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

interface SectionRowProps {
  readonly icon: ReactNode;
  readonly title: string;
  readonly subtitle: string;
  readonly onClick?: () => void;
}

function SectionRow({
  icon,
  title,
  subtitle,
  onClick,
}: SectionRowProps): JSX.Element {
  const interactive = onClick !== undefined;
  return (
    <button
      type="button"
      className={styles.row}
      onClick={onClick}
      disabled={!interactive}
    >
      <span className={styles.rowChip} aria-hidden="true">
        {icon}
      </span>
      <span className={styles.rowText}>
        <span className={styles.rowTitle}>{title}</span>
        <span className={styles.rowSubtitle}>{subtitle}</span>
      </span>
      <span className={styles.rowChevron}>
        <Chevron />
      </span>
    </button>
  );
}

export function HomePage(): JSX.Element {
  const navigate = useNavigate();
  // Root screen — no back button.
  useBackButton(() => undefined, { visible: false });

  const { data: me, isLoading, error, refetch } = useMe();

  if (isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (error || !me) {
    return (
      <StatePanel
        kind="error"
        message="Не удалось загрузить профиль. Откройте приложение через Telegram."
        onRetry={refetch}
      />
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.logo} aria-hidden="true">
          {me.branding.logo ? (
            <img
              src={me.branding.logo}
              alt=""
              className={styles.logoImg}
              draggable={false}
            />
          ) : (
            <span className={styles.logoFallback}>M</span>
          )}
        </span>
        <div className={styles.brand}>
          <span className={styles.brandName}>Melusine Shop</span>
          <span className={styles.brandTag}>Mini App</span>
        </div>
        <button
          type="button"
          className={styles.menuButton}
          aria-label="Поддержка"
          onClick={() => {
            navigate(routes.support());
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M4 7h16" />
            <path d="M4 12h16" />
            <path d="M4 17h16" />
          </svg>
        </button>
      </header>

      <BotActivationBanner me={me} />

      <ShopClosedBanner me={me} />

      {me.shop_public_url ? (
        <button
          type="button"
          className={styles.referralStrip}
          onClick={() => {
            openTelegramLink(me.shop_public_url);
          }}
        >
          <span className={styles.referralIcon} aria-hidden="true">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 11l18-5v12L3 14v-3z" />
              <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
            </svg>
          </span>
          <span className={styles.referralText}>
            <span className={styles.referralTitle}>Паблик магазина</span>
            <span className={styles.referralSubtitle}>
              Официальное сообщество магазина с информацией об обновлениях,
              новостях и розыгрышах.
            </span>
          </span>
          <span className={styles.referralValue}>
            <Chevron />
          </span>
        </button>
      ) : null}

      <ProfileCard
        me={me}
        onReferralsClick={() => {
          navigate(routes.referrals());
        }}
      />

      <BalanceCard balance={me.balance} />

      <button
        type="button"
        className={styles.shopTile}
        onClick={() => {
          navigate(routes.shop());
        }}
      >
        <span className={styles.shopIcon} aria-hidden="true">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 2 3 7v13a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V7l-3-5z" />
            <path d="M3 7h18" />
            <path d="M16 11a4 4 0 0 1-8 0" />
          </svg>
        </span>
        <span className={styles.shopText}>
          <span className={styles.shopTitle}>Магазин</span>
          <span className={styles.shopSubtitle}>Донат и игровые услуги</span>
        </span>
        <span className={styles.shopChevron}>
          <Chevron />
        </span>
      </button>

      <button
        type="button"
        className={styles.rpgTile}
        onClick={() => {
          navigate(routes.rpg());
        }}
      >
        <span className={styles.rpgIcon} aria-hidden="true">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
            <path d="m13 19 6-6" />
            <path d="m16 16 4 4" />
            <path d="M21 3v3l-9 9" />
            <path d="m5 13-2 2" />
          </svg>
        </span>
        <span className={styles.shopText}>
          <span className={styles.rpgTitle}>Mini-RPG</span>
          <span className={styles.rpgSubtitle}>
            Бои, награды, мора и примогемы
          </span>
        </span>
        <span className={styles.rpgChevron}>
          <Chevron />
        </span>
      </button>

      <div className={styles.sections}>
        <SectionRow
          title="Мои рефералы"
          subtitle="Ссылка и звёздный блеск"
          onClick={() => {
            navigate(routes.referrals());
          }}
          icon={
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="8" width="18" height="4" rx="1" />
              <path d="M12 8v13" />
              <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
              <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8" />
              <path d="M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" />
            </svg>
          }
        />
        <SectionRow
          title="Мои заказы"
          subtitle="Статусы, чат и история"
          onClick={() => {
            navigate(routes.orders());
          }}
          icon={
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1z" />
              <path d="M8 7h8" />
              <path d="M8 11h8" />
              <path d="M8 15h5" />
            </svg>
          }
        />
        {/* «Отзывы» оставляются по заказу/товару (нет глобальной страницы в пилоте). */}
        <SectionRow
          title="Поддержка"
          subtitle="Чат с продавцом"
          onClick={() => {
            navigate(routes.support());
          }}
          icon={
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
            </svg>
          }
        />
        <SectionRow
          title="Настройки"
          subtitle="Тема приложения"
          onClick={() => {
            navigate(routes.settings());
          }}
          icon={
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          }
        />
      </div>
    </div>
  );
}
